import { gateAllows } from '../../utils/hubs/registry';
import { cultureScore, detectMode, microscopyScore, thermalScore } from './detect';

/*
 * The analysis workbench's modes as data (docs/ANALYSIS_WORKBENCH.md section
 * 3): everything about a mode but its component. ./modes.js adds each mode's
 * Component and is what the workbench page reads; this list is for pages
 * outside the workbench that send people into it (Home's image source picker,
 * components/hub/ImageSourcePicker.js), so they ask the same gates and the
 * same detection without pulling the modes' code into their own bundle (the
 * workbench page is loaded lazily, App.js).
 *
 *   key         ?mode=<key>
 *   label       i18n key (namespace workbench)
 *   icon        beside the label
 *   gate        who may open the mode (a utils/hubs/registry.js gate); the
 *               route's own gate (registry.js WORKBENCH_GATE) is any of them
 *   writeGate   who may hand it a file (Open a file, an upload)
 *   params      the mode's own address params; the shell keeps `mode` and
 *               `drawers`, every other param is the mode's
 *   legacy      the page it replaced (its address redirects: legacy.js), or
 *               the page it still opens while it has no Component
 *   legacyParams  for a mode not moved yet: the params handed to its page
 *   detect      source -> 0..1 (detect.js)
 */
export const MODE_LIST = Object.freeze([
  {
    key: 'culture',
    label: 'modes.culture',
    icon: '🧫',
    gate: 'lab:read',
    writeGate: 'lab:observe',
    // camera=1: the dish camera opens on arrival (Home's image source picker), then leaves the address.
    params: ['plate', 'photo', 'src', 'isolate', 'tray', 'camera'],
    legacy: '/tools/culture-analysis',
    detect: cultureScore,
  },
  {
    key: 'thermal',
    label: 'modes.thermal',
    icon: '🌡️',
    gate: 'lab:read',
    writeGate: 'lab:observe',
    // capture: the one on the stage; isolate: new ROIs tied to it; experiment: its ROI template and seed;
    // measure (part 4): the region whose growth is measured on the photo, measure_plate the isolate's plate chosen.
    params: ['isolate', 'capture', 'experiment', 'measure', 'measure_plate'],
    legacy: '/tools/thermal',
    detect: thermalScore,
  },
  {
    key: 'microscopy',
    label: 'modes.microscopy',
    icon: '🔬',
    gate: 'game:play',
    // A file handed to it is uploaded as a micrograph: lab:observe, as the Source drawer's Upload tab.
    writeGate: 'lab:observe',
    params: ['image', 'isolate'],
    legacy: '/tools/spore-analysis',
    detect: microscopyScore,
  },
]);

export const modeOf = key => MODE_LIST.find(m => m.key === key) || null;

/** The modes this person may open, in the switcher's order. */
export const availableModes = user => MODE_LIST.filter(m => gateAllows(user, m.gate));

/** The modes this person may open and hand a file to. */
export const fileModes = user => MODE_LIST.filter(m => gateAllows(user, m.gate) && gateAllows(user, m.writeGate));

/**
 * The mode a source belongs to, among those this person may open and hand a
 * file to; null for none (the caller stays where it is).
 */
export function detectModeFor(source, user) {
  return detectMode(source, fileModes(user));
}

/**
 * Where a file handed in from outside the workbench goes (Home's image source
 * picker): the mode detection names, else -- as the workbench's own Open a
 * file falls back to the mode open -- the one that scores it highest above
 * nothing: a plain picture (0.3) is a dish photograph for the culture mode, a
 * FLIR camera's visual photograph (0.4) the thermal mode's. Null when no mode
 * of theirs takes it at all (a text file).
 */
export function handedFileMode(source, user) {
  const named = detectModeFor(source, user);
  if (named) return named;
  let best = null;
  let bestScore = 0;
  for (const mode of fileModes(user)) {
    const score = Number(mode.detect(source)) || 0;
    if (score > bestScore) {
      best = mode.key;
      bestScore = score;
    }
  }
  return best;
}
